import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react'

import { Page } from '@/shared/components/layers/Page/Page'
import { PIN_REQUIRED_EVENT } from '@/shared/model/auth.events'
import { useAuth } from '../../lib/useAuth'
import { useAuthActions } from '../../lib/useAuthActions'
import css from './PinGate.module.css'

function errorMessage(error: unknown): string {
  const responseMessage = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message
  return typeof responseMessage === 'string' ? responseMessage : 'Не удалось проверить PIN'
}

export function PinGate({ children }: { children: ReactNode }) {
  const { isAuth, user, pinUnlocked, setPinUnlocked, setUser } = useAuth()
  const { setupPin, verifyPin, resetPin, refresh, logout } = useAuthActions()
  const [pin, setPin] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resetMode, setResetMode] = useState(false)
  const [password, setPassword] = useState('')
  const isSetup = user?.pin_configured === false
  const needsConfirmation = isSetup || resetMode

  useEffect(() => {
    const lock = () => {
      setPinUnlocked(false)
      setPin('')
      setConfirmation('')
      setError(null)
      setResetMode(false)
      setPassword('')
      void refresh()
    }
    window.addEventListener(PIN_REQUIRED_EVENT, lock)
    return () => window.removeEventListener(PIN_REQUIRED_EVENT, lock)
  }, [setPinUnlocked, refresh])

  const valid = useMemo(
    () => /^\d{4}$/.test(pin) && (!needsConfirmation || pin === confirmation) && (!resetMode || password.length > 0),
    [pin, confirmation, needsConfirmation, resetMode, password]
  )

  if (!isAuth || pinUnlocked) return <>{children}</>

  const attempt = async (pinValue: string) => {
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      if (resetMode) await resetPin(password, pinValue)
      else if (isSetup) await setupPin(pinValue)
      else await verifyPin(pinValue)
    } catch (requestError) {
      const message = errorMessage(requestError)
      if (message.includes('Сначала настройте PIN') && user) {
        setUser({ ...user, pin_configured: false })
        setError('PIN был сброшен. Задайте новый код.')
      } else {
        setError(message)
      }
      setPin('')
      if (!isSetup) setConfirmation('')
    } finally {
      setLoading(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid || loading) return
    await attempt(pin)
  }

  const digitsOnly = (value: string) => value.replace(/\D/g, '').slice(0, 4)

  return (
    <Page title="OKG: PIN">
      <div className={css.page}>
        <form className={css.form} onSubmit={submit}>
          <div className={css.lock} aria-hidden="true">🔒</div>
          <h1 className={css.title}>{resetMode ? 'Сброс PIN' : (isSetup ? 'Создайте PIN' : 'Введите PIN')}</h1>
          <p className={css.description}>
            {resetMode
              ? 'Введите пароль от аккаунта и задайте новый PIN.'
              : isSetup
              ? 'Он будет защищать план продаж на этом и других ваших устройствах.'
              : `Для доступа к плану продаж${user?.email ? ` — ${user.email}` : ''}`}
          </p>
          {resetMode && <>
            <label className={css.label} htmlFor="pin-password">Текущий пароль</label>
            <input
              id="pin-password"
              className={css.password}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              disabled={loading}
              autoFocus
            />
          </>}
          <label className={css.label} htmlFor="plan-pin">{resetMode ? 'Новый PIN — 4 цифры' : '4 цифры'}</label>
          <input
            id="plan-pin"
            className={css.pin}
            type="password"
            inputMode="numeric"
            pattern="[0-9]{4}"
            autoComplete={needsConfirmation ? 'new-password' : 'current-password'}
            value={pin}
            onChange={event => {
              const nextPin = digitsOnly(event.target.value)
              setPin(nextPin)
              if (nextPin.length === 4 && !isSetup && !resetMode && !loading) {
                void attempt(nextPin)
              }
            }}
            maxLength={4}
            disabled={loading}
            autoFocus={!resetMode}
          />
          {needsConfirmation && (
            <>
              <label className={css.label} htmlFor="plan-pin-confirm">Повторите PIN</label>
              <input
                id="plan-pin-confirm"
                className={css.pin}
                type="password"
                inputMode="numeric"
                pattern="[0-9]{4}"
                autoComplete="new-password"
                value={confirmation}
                onChange={event => setConfirmation(digitsOnly(event.target.value))}
                maxLength={4}
                disabled={loading}
              />
              {confirmation.length === 4 && pin !== confirmation && (
                <p className={css.error} role="alert">PIN-коды не совпадают</p>
              )}
            </>
          )}
          {error && <p className={css.error} role="alert">{error}</p>}
          {(isSetup || resetMode) && <button className={css.submit} type="submit" disabled={!valid || loading}>
            {loading ? 'Проверяем…' : (resetMode ? 'Задать новый PIN' : 'Сохранить PIN')}
          </button>}
          {!isSetup && !resetMode && loading && <p className={css.checking} role="status">Проверяем PIN…</p>}
          {!isSetup && <button className={css.logout} type="button" onClick={() => {
            setResetMode(value => !value)
            setPin('')
            setConfirmation('')
            setPassword('')
            setError(null)
          }} disabled={loading}>
            {resetMode ? 'Вернуться к вводу PIN' : 'Забыли PIN?'}
          </button>}
          <button className={css.logout} type="button" onClick={() => logout()} disabled={loading}>
            Выйти из аккаунта
          </button>
        </form>
      </div>
    </Page>
  )
}
